import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import api from "../../api/axios";
import { Pencil, Trash, Folder, PlusCircle } from "lucide-react";
import SubCompanyForm from "../../components/admin/SubCompanyForm";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import { useMainCompany } from '../../context/MainCompanyContext';
import Toast from "../../components/common/Toast";

export default function AdminCompany() {

  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  // selectedMain provided by MainCompanyContext (used only as fallback)
  const { selectedMain } = useMainCompany();
  // Prefer maincompanyid supplied via URL (route param `id`) — fallback to header-selected main company
  const params = useParams();
  const urlMainId = params?.id || params?.maincid || params?.mainCompanyId || params?.mainCompany || params?.companyId;
  const mainCompanyId = Number(urlMainId) || selectedMain;
  const [stateId, setStateId] = useState('');
  const [countyId, setCountyId] = useState('');
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [, setLoadingStates] = useState(false);
  const [, setLoadingCounties] = useState(false);
  // Create/edit modal state (kept minimal; form is implemented in SubCompanyForm component)
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [createStateId] = useState(0);
  const [, setCreateCounties] = useState([]);
  // Edit modal state
  const [editId, setEditId] = useState(0);
  const [editStateId, setEditStateId] = useState(0);
  const [editCountyId, setEditCountyId] = useState(0);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editstate_based_tax, setEditstate_based_tax] = useState(false);
  const [editcounty_based_tax, setEditcounty_based_tax] = useState(false);
  // Toggle status confirmation/modal state
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [toggleTargetId, setToggleTargetId] = useState(0);
  const [toggleTargetActive, setToggleTargetActive] = useState(false);
  const [toggling, setToggling] = useState(false);
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteErrors, setDeleteErrors] = useState('');
  // simple in-component toast
  const [toast, setToast] = useState({ message: '', type: 'error', visible: false });

  const showToast = (message, type = 'error', duration = 4000) => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  const [filters, setFilters] = useState({
    company: "",
    created: "",
  });

  // filter handler removed (filters UI currently commented out)

  const filteredCompanies = companies.filter((c) => {
    // filter by company_name and created only
    if (filters.company && !String(c.company_name || "").toLowerCase().includes(filters.company.toLowerCase())) return false;
    if (filters.created && !String(c.created || "").includes(filters.created)) return false;
    return true;
  });

  // keep latest page/limit in refs so fetchCompanies doesn't need them as dependencies
  // (including them would change fetchCompanies' identity on every page/limit change,
  // which would re-trigger effects that depend on fetchCompanies below)
  const pageRef = useRef(page);
  const limitRef = useRef(limit);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { limitRef.current = limit; }, [limit]);

  // fetch companies (reusable) — accepts tentative page/limit and only commits on success
  const fetchCompanies = useCallback(async (tentativePage = null, tentativeLimit = null) => {
    setLoading(true);
    try {
      const params = {};
      if (stateId) params.stateid = stateId;
      if (countyId) params.countyid = countyId;
      // decide which page/limit to request
      const reqPage = Number(tentativePage ?? pageRef.current) || 1;
      const reqLimit = Number(tentativeLimit ?? limitRef.current) || 10;
      params.page = reqPage;
      params.limit = reqLimit;
      const path = mainCompanyId ? `/admin/sub-companies/${mainCompanyId}` : `/admin/sub-companies`;
      const resp = await api.get(path, { params });
      const body = resp.data;
      let items = [];
      let meta = { total: 0, page: reqPage, limit: reqLimit };
      // Expected new shape: { status, message, data: { data: [...], meta: { total, page, limit } } }
      if (body && body.data && body.data.data && Array.isArray(body.data.data)) {
        items = body.data.data;
        if (body.data.meta) meta = { ...meta, ...body.data.meta };
      } else if (body && Array.isArray(body.data)) {
        // older shape: { data: [...] }
        items = body.data;
        meta = { total: items.length, page: reqPage, limit: reqLimit };
      } else if (Array.isArray(body)) {
        items = body;
        meta = { total: items.length, page: reqPage, limit: reqLimit };
      } else if (body && typeof body === 'object' && body.id) {
        items = [body];
        meta = { total: 1, page: reqPage, limit: reqLimit };
      }
      // commit only after successful fetch
      setCompanies(items);
      setTotal(Number(meta.total) || 0);
      setPage(Number(meta.page) || reqPage);
      setLimit(Number(meta.limit) || reqLimit);
      return { success: true, meta };
    } catch (e) {
      console.error('Error fetching sub-companies', e?.response?.status, e?.response?.data || e.message);
      // Do not commit page/limit on failure
      setCompanies((prev) => prev || []);
      // show toast to surface the error
      const msg = e?.response?.data?.message || e?.response?.data || e?.message || 'Failed to load data';
      showToast(msg, 'error');
      return { success: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [mainCompanyId, stateId, countyId]);

  // initial load and whenever the main company / state / county filters change — reset to page 1
  useEffect(() => { fetchCompanies(1); }, [mainCompanyId, stateId, countyId, fetchCompanies]);

  // reset delete modal fields when it opens/closes
  useEffect(() => {
    if (showDeleteConfirm) {
      setDeletePassword('');
      setDeleteErrors('');
    }
  }, [showDeleteConfirm]);

  // Open edit modal using the listing row data (no extra GET for row)
  const openEdit = async (row) => {
    if (!row) return;
    const stateVal = row.stateid || row.state_id || row.stateId || row.state || 0;
    const countyVal = row.countyid || row.county_id || row.countyId || row.county || 0;
    setEditId(row.id);
    setEditCompanyName(row.company_name || '');
    setEditstate_based_tax(row.state_based_tax || false);
    setEditcounty_based_tax(row.county_based_tax || false);
    const sVal = Number(stateVal) || 0;
    const ctVal = Number(countyVal) || 0;
    setEditStateId(sVal);
    // Do not call APIs when opening edit — listing provides state/county names
    if (ctVal) setEditCountyId(ctVal);
    setShowEdit(true);
  };

  // Load states on mount and whenever the page's main company (from URL or fallback) changes
  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        try {
          // clear dependent filters when the page's main company changes
          setStateId(0);
          setCountyId(0);
          const params = {};
          if (mainCompanyId) params.maincompanyid = mainCompanyId;
          const resp = await api.get('/admin/states', { params });
          const body = resp.data;
          const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
          setStates(items);
        } catch (e) {
          console.error('Failed loading states', e?.response?.status, e?.response?.data || e.message);
          setStates([]);
        }
      } catch (e) {
        console.error('Failed loading states', e);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, [mainCompanyId]);

  // Ensure select and option text is visible (force black on options where browsers may inherit white)
  useEffect(() => {
    const css = `.admin-company-page select, .admin-company-page select option { color: #000 !important; background: #fff !important; }`;
    const style = document.createElement('style');
    style.setAttribute('data-generated', 'admin-company-select');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
    return () => {
      try { document.head.removeChild(style); } catch (e) { }
    };
  }, []);

  // Load counties whenever stateId or the page main company changes
  useEffect(() => {
    const loadCounties = async () => {
      if (!stateId) return setCounties([]);
      setLoadingCounties(true);
      try {
        try {
          const params = { stateId };
          if (mainCompanyId) params.maincompanyid = mainCompanyId;
          const resp = await api.get('/auth/counties', { params });
          const body = resp.data;
          const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
          setCounties(items);
        } catch (e) {
          console.error('Failed loading counties', e?.response?.status, e?.response?.data || e.message);
          setCounties([]);
        }
      } catch (e) {
        console.error('Failed loading counties', e);
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    loadCounties();
  }, [stateId, mainCompanyId]);

  // When the page-level state filter changes, clear any previously selected county
  const handleStateFilterChange = (value) => {
    const id = Number(value) || 0;
    setStateId(id);
    // clear dependent county filter and list to avoid stale selections
    setCountyId(0);
    setCounties([]);
  };

  // Load counties for the create modal when createStateId changes
  useEffect(() => {
    const load = async () => {
      if (!createStateId) return setCreateCounties([]);
      try {
        const params = { stateId: createStateId };
        if (mainCompanyId) params.maincompanyid = mainCompanyId;
        const resp = await api.get('/auth/counties', { params });
        const body = resp.data;
        const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
        setCreateCounties(items);
      } catch (e) {
        console.error('Failed loading create modal counties', e?.response?.data || e.message);
        setCreateCounties([]);
      }
    };
    load();
  }, [createStateId, mainCompanyId]);

  // small date formatter: fall back to raw value if invalid
  const formatDate = (d) => {
    try {
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      // Format like: Sep 05, 2025
      return dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    } catch (e) {
      return d || "";
    }
  };

  // format property_tax column values: support array of primitives or objects
  const formatPropertyTax = (pt) => {
    try {
      if (pt === null || pt === undefined) return '';
      if (Array.isArray(pt)) {
        const items = pt.map((p) => {
          if (p === null || p === undefined) return '';
          if (typeof p === 'object') {
            // prefer common fields, fall back to JSON string
            return p.name || p.label || p.value || p.tax || (Object.keys(p).length ? String(p) : '');
          }
          return String(p);
        }).filter(Boolean);
        if (items.length === 0) return 'N/A';
        return items.join(', ');
      }
      // single value
      if (typeof pt === 'object') {
        if (Object.keys(pt).length === 0) return 'N/A';
        return pt.name || pt.label || pt.value || pt.tax || String(pt);
      }
      return String(pt);
    } catch (e) {
      return '';
    }
  };

  // format county_based_tax: if string 'true' (or boolean true) show Yes else No
  const formatCountyBasedTax = (v) => {
    try {
      if (v === true || v === 1 || v === '1') return 'Yes';
      const s = (v === null || v === undefined) ? '' : String(v).trim().toLowerCase();
      if (s === 'true' || s === '1') return 'Yes';
      return 'No';
    } catch (e) {
      return 'No';
    }
  };

  const handleRedirect = (id, maincompanyid) => {
    navigate(`/admin/company-settings/${maincompanyid}/${id}`);
  };

  // normalize various shapes of is_active (string '1', number 1, boolean true)
  const isActiveFlag = (v) => (v === '1' || v === 1 || v === true);

  return (
    <div className="p-4 admin-company-page">
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      <AdminPageHeader
        title="Manage Sub Companies"
        actions={(
          <div className="flex gap-2 items-center">
            <button className={`flex items-center gap-1 px-3 py-2 text-sm rounded transition bg-blue-600 text-white hover:bg-blue-700`} onClick={() => setShowCreate(true)}>
              <PlusCircle className="w-4 h-4" /> Create Sub Company
            </button>
          </div>
        )}
        filters={(
          <div className="flex gap-2 items-center">
            <select value={stateId} onChange={(e) => handleStateFilterChange(e.target.value)} className="border px-2 py-1 rounded text-sm bg-white text-black w-56">
              <option value={0}>All States</option>
              {states.map((s) => (
                <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>{s.statename || s.name}</option>
              ))}
            </select>
            <select value={countyId} onChange={(e) => setCountyId(Number(e.target.value) || 0)} className="border px-2 py-1 rounded text-sm bg-white text-black w-56">
              <option value={0}>All Counties</option>
              {counties.map((c) => (
                <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>{c.countyname || c.name}</option>
              ))}
            </select>
          </div>
        )}
      />

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-2 py-2 text-left">Company</th>
              <th className="px-2 py-2 text-left">Property Tax</th>
              <th className="px-2 py-2 text-left">County Setting</th>
              <th className="px-2 py-2 text-left">Created Date</th>
              <th className="px-2 py-2 text-center">Action</th>
            </tr>

            {/* Filters */}
            {/* <tr className="bg-gray-50">
              <th className="px-2 py-2">
                <input
                  type="text"
                  placeholder="Filter Company"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={filters.company}
                  onChange={(e) => handleFilterChange('company', e.target.value)}
                />
              </th>
              <th className="px-2 py-2">
                <input
                  type="date"
                  value={filters.created}
                  onChange={(e) => handleFilterChange('created', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </th>
              <th className="px-2 py-2">
                <button
                  onClick={() => setFilters({ company: '', created: '' })}
                  className="ml-1 flex items-center gap-1 px-3 py-1 border rounded text-sm text-gray-700 hover:bg-gray-100"
                >
                  Reset
                </button>
              </th>
            </tr> */}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-gray-500">Loading...</td>
              </tr>
            ) : filteredCompanies.length > 0 ? (
              filteredCompanies.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-2 align-middle">
                    <div className="font-medium text-sm">{c.company_name}</div>
                    {/* <div className="text-xs text-gray-500">State: {c.stateid || c.state || ''} County: {c.countyid || c.county || ''}</div> */}
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <div className="text-sm text-gray-500">{formatPropertyTax(c.property_tax)}</div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="text-sm text-gray-500">{formatCountyBasedTax(c.county_based_tax)}</div>
                  </td>
                  <td className="px-2 py-2 align-middle">{formatDate(c.created)}</td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex gap-2">
                      <button onClick={() => { const isActive = isActiveFlag(c.is_active); setToggleTargetId(c.id); setToggleTargetActive(isActive); setShowToggleConfirm(true); }} className="px-3 py-1 text-xs bg-gray-200 text-black rounded hover:bg-gray-300">{isActiveFlag(c.is_active) ? 'Disable' : 'Enable'}</button>
                      <button onClick={() => openEdit(c)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={() => { setDeleteTargetId(c.id); setShowDeleteConfirm(true); }} className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"><Trash className="w-3 h-3" /> Delete</button>
                      <button onClick={() => handleRedirect(c.id, c.maincompanyid)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"><Folder className="w-3 h-3" /> Data</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-3 text-gray-500">No results found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {Math.ceil((total || companies.length) / limit) > 1 && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <span>
            Showing {(page - 1) * limit + 1}–{Math.min((page) * limit, total || companies.length)} of {total || companies.length}
          </span>
          <div className="flex gap-2 items-center">
            <label>Rows</label>
            <select value={limit} onChange={async (e) => { const newLimit = Number(e.target.value) || 10; const resp = await fetchCompanies(1, newLimit); if (resp.success) { /* limit and page committed inside fetchCompanies */ } }} className="border rounded px-2 py-1">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button onClick={async () => { const target = Math.max(1, page - 1); await fetchCompanies(target); }} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">Prev</button>
            <span>{page} / {Math.ceil((total || companies.length) / limit)}</span>
            <button onClick={async () => { const last = Math.ceil((total || companies.length) / limit) || 1; const target = Math.min(last, page + 1); await fetchCompanies(target); }} disabled={page >= Math.ceil((total || companies.length) / limit)} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">Next</button>
          </div>
        </div>
      )}
      {/* Create Modal — reuse SubCompanyForm */}
      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-2xl p-4">
            <h2 className="text-lg font-semibold mb-3">Create Sub-Company</h2>
            <SubCompanyForm
              mode="create"
              mainCompanyId={mainCompanyId}
              onClose={() => setShowCreate(false)}
              onCreated={async () => {
                setShowCreate(false);
                const resp = await fetchCompanies();
                if (resp && resp.success) showToast('Created successfully', 'success');
                else showToast('Created, but refresh failed', 'error');
              }}
            />
          </div>
        </div>
      )}
      {/* Delete confirm modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-sm p-4">
            <h3 className="text-lg font-semibold">Delete Sub-Company</h3>
            <p className="mt-2">Are you sure you want to permanently delete this sub-company?</p>
            {/* wrap in a form with autocomplete off to discourage browser autofill */}
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <div className="mt-3">
                <label className="block text-sm">Enter delete password</label>
                <input
                  type="password"
                  name={`delete-password-${deleteTargetId}`}
                  autoComplete="new-password"
                  spellCheck="false"
                  autoCorrect="off"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full border px-2 py-1 rounded mt-1"
                />
                {deleteErrors && <div className="text-red-600 text-sm mt-2">{deleteErrors}</div>}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1 border rounded">Cancel</button>
                <button type="button" onClick={async () => {
                  setDeleting(true);
                  try {
                    // axios delete with request body: use { data: { password } }
                    await api.delete(`/admin/sub-companies/${deleteTargetId}`, { data: { password: deletePassword }, skipAuthRedirect: true });
                    const resp = await fetchCompanies();
                    // clear company filter after successful delete
                    setFilters((f) => ({ ...f, company: '' }));
                    setShowDeleteConfirm(false);
                    setDeletePassword('');
                    setDeleteErrors('');
                    if (resp && resp.success) showToast('Deleted successfully', 'success');
                  } catch (e) {
                    console.error('Delete failed', e?.response?.data || e.message);
                    const status = e?.response?.status;
                    const msg = e?.response?.data?.message || e?.response?.data || e?.message || 'Delete failed';
                    // show toast for general failures to avoid redirect-to-login behavior
                    showToast(msg, 'error');
                    if (status === 403) {
                      setDeleteErrors(msg || 'Invalid delete password');
                    } else {
                      setDeleteErrors(msg);
                    }
                  } finally {
                    setDeleting(false);
                  }
                }} className="px-3 py-1 bg-red-600 text-white rounded">{deleting ? 'Deleting...' : 'Delete'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Toggle confirm modal */}
      {showToggleConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-sm p-4">
            <h3 className="text-lg font-semibold">Confirm</h3>
            <p className="mt-2">Are you sure you want to {toggleTargetActive ? 'disable' : 'enable'} this sub-company?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowToggleConfirm(false)} className="px-3 py-1 border rounded">Cancel</button>
              <button onClick={async () => {
                setToggling(true);
                try {
                  await api.post(`/admin/sub-companies/${toggleTargetId}/toggle-status`, { is_active: !toggleTargetActive });
                  // refresh list from server to ensure canonical state
                  const resp = await fetchCompanies();
                  setShowToggleConfirm(false);
                  if (resp && resp.success) showToast('Updated status', 'success');
                } catch (e) {
                  console.error('Toggle failed', e?.response?.data || e.message);
                } finally {
                  setToggling(false);
                }
              }} className="px-3 py-1 bg-red-600 text-white rounded">{toggling ? 'Please wait...' : (toggleTargetActive ? 'Disable' : 'Enable')}</button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Modal — reuse SubCompanyForm */}
      {showEdit && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-2xl p-4">
            <h2 className="text-lg font-semibold mb-3">Edit Sub-Company</h2>
            <SubCompanyForm
              mode="edit"
              mainCompanyId={mainCompanyId}
              initial={{
                id: editId,
                company_name: editCompanyName,
                stateid: editStateId,
                countyid: editCountyId,
                // pass names from listing row if available to avoid extra fetches
                statename: companies.find(c => c.id === editId)?.statename || companies.find(c => c.id === editId)?.state_name || companies.find(c => c.id === editId)?.state || '',
                countyname: companies.find(c => c.id === editId)?.countyname || companies.find(c => c.id === editId)?.county_name || companies.find(c => c.id === editId)?.county || '',
                state_based_tax: editstate_based_tax,
                county_based_tax: editcounty_based_tax,
                property_tax: companies.find(c => c.id === editId)?.property_tax || []
              }}
              onClose={() => setShowEdit(false)}
              onCreated={async () => { setShowEdit(false); const resp = await fetchCompanies(); if (resp && resp.success) showToast('Saved successfully', 'success'); else showToast('Saved, but refresh failed', 'error'); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
